import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ToastProvider, createSupabaseClient, AuthCacheReset, setGapiBase } from '@dogdisk/shared'
import './index.css'

// gapi 组前缀:agent SPA 挂在 /gapi/agent router group(gapi-restful-regroup)。
setGapiBase('/gapi/agent')

import { AgentLoginPage } from './routes/login'
import { AgentDashboardPage } from './routes/dashboard'
import { AgentStatsPage } from './routes/stats'
import { AgentUsersPage } from './routes/users'
import { AgentTransactionsPage } from './routes/transactions'
import { AgentSubAgentsPage } from './routes/sub-agents'
import { AgentSubAgentSettingsPage } from './routes/sub-agent-settings'
import { AgentWithdrawPage } from './routes/withdraw'
import { AgentDomainGuardKeysPage } from './routes/domain-guard-keys'
import { AgentDomainGuardDomainsPage } from './routes/domain-guard-domains'
import { AgentSettingsPage } from './routes/settings'
import { AgentLoginHistoryPage } from './routes/login-history'
import { AgentRecommendSettingsPage } from './routes/recommend-settings'
import { AgentAdsSlotPage } from './routes/ads-slot'
import { AgentAdsReportPage } from './routes/ads-report'

// VITE_SUPABASE_URL 空 → 默认同源（Caddy 反代 supabase 端点到 Kong）
const supabasePublishableKey = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']
if (import.meta.env.PROD && !supabasePublishableKey) {
  throw new Error('VITE_SUPABASE_PUBLISHABLE_KEY 缺失:生产构建必须 docker build --build-arg 注入')
}
createSupabaseClient({
  url: import.meta.env['VITE_SUPABASE_URL'] || window.location.origin,
  anonKey: supabasePublishableKey ?? '',
})

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
})

const el = document.getElementById('root')
if (!el) throw new Error('#root not found')

createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthCacheReset />
      <ToastProvider>
        <HashRouter>
          <Routes>
            <Route path="/login" element={<AgentLoginPage />} />
            <Route path="/" element={<AgentDashboardPage />} />
            <Route path="/stats" element={<AgentStatsPage />} />
            <Route path="/users" element={<AgentUsersPage />} />
            <Route path="/transactions" element={<AgentTransactionsPage />} />
            <Route path="/withdraw" element={<AgentWithdrawPage />} />
            <Route path="/sub-agent-settings" element={<AgentSubAgentSettingsPage />} />
            {/* 旧路径保留：自动重定向到 /sub-agent-settings */}
            <Route path="/sub-agents" element={<AgentSubAgentsPage />} />
            <Route path="/settings" element={<AgentSettingsPage />} />
            <Route path="/domain-guard/keys" element={<AgentDomainGuardKeysPage />} />
            <Route path="/domain-guard/domains" element={<AgentDomainGuardDomainsPage />} />
            {/* 旧路径保留：重定向到拆分后的密钥配置页 */}
            <Route path="/settings-advanced" element={<Navigate to="/domain-guard/keys" replace />} />
            <Route path="/login-history" element={<AgentLoginHistoryPage />} />
            <Route path="/recommend-settings" element={<AgentRecommendSettingsPage />} />
            {/* 广告管理 */}
            <Route
              path="/ads/forum-icon"
              element={<AgentAdsSlotPage slotType="forum_list_icon" />}
            />
            <Route
              path="/ads/forum-card"
              element={<AgentAdsSlotPage slotType="forum_list_card" />}
            />
            <Route
              path="/ads/article-banner"
              element={<AgentAdsSlotPage slotType="article_banner" />}
            />
            <Route path="/ads/report" element={<AgentAdsReportPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </HashRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
)
